import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { FolderKanban, Loader } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { useBranding } from "../hooks/useBranding";
import { anexoUrlAbsoluto } from "../services/tarefas";

// Chave legada (só e-mail) — migrada na primeira leitura pro novo formato.
const KEY_LEMBRAR_EMAIL = "growsoft_lembrar_email";
// Novo: guarda { email, senha } no aparelho. AVISO: localStorage NÃO é cofre —
// senha vira reversível pra qualquer script rodando no mesmo origin. Aceitamos
// porque é um sistema interno e o usuário escolhe explicitamente via checkbox.
const KEY_LEMBRAR_CREDS = "growsoft_lembrar_creds";

interface CredsSalvas { email: string; senha: string }
function lerCreds(): CredsSalvas | null {
  try {
    const raw = localStorage.getItem(KEY_LEMBRAR_CREDS);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && typeof obj.email === "string" && typeof obj.senha === "string") return obj;
    }
    // Fallback pro formato antigo (só e-mail)
    const emailAntigo = localStorage.getItem(KEY_LEMBRAR_EMAIL);
    if (emailAntigo) return { email: emailAntigo, senha: "" };
  } catch {}
  return null;
}
function gravarCreds(c: CredsSalvas | null) {
  try {
    if (c) localStorage.setItem(KEY_LEMBRAR_CREDS, JSON.stringify(c));
    else localStorage.removeItem(KEY_LEMBRAR_CREDS);
    // Limpa a chave legada quando passar a usar a nova
    localStorage.removeItem(KEY_LEMBRAR_EMAIL);
  } catch {}
}

export function Login() {
  const { login } = useAuth();
  const branding = useBranding();
  const credsIniciais = lerCreds();
  const [email, setEmail] = useState(credsIniciais?.email || "");
  const [senha, setSenha] = useState(credsIniciais?.senha || "");
  const [lembrar, setLembrar] = useState(!!credsIniciais);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Mantém storage em sync enquanto o usuário digita (quando marcado).
  useEffect(() => {
    if (!lembrar) return;
    gravarCreds({ email, senha });
  }, [email, senha, lembrar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (lembrar) gravarCreds({ email, senha });
    else gravarCreds(null);

    const result = await login(email, senha);
    if (!result.success) {
      setError(result.error || "Erro ao fazer login");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle className="p-2 rounded-lg bg-slate-800/60 hover:bg-slate-800 text-slate-200 transition-colors" />
      </div>
      <div className="w-full max-w-md p-4">
        {/* Logo */}
        <div className="text-center mb-8">
          {branding?.logoUrl ? (
            // Logo cheia, sem o container azul — a marca aparece pura.
            <div className="mx-auto mb-4 flex items-center justify-center" style={{ maxHeight: 96 }}>
              <img
                src={anexoUrlAbsoluto(branding.logoUrl)}
                alt={branding?.nome || "RECDC OS"}
                className="max-h-24 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-4">
              <FolderKanban size={32} className="text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{branding?.nome || "RECDC OS"}</h1>
          <p className="text-slate-400 mt-1">Gestão de projetos e prazos</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-xl shadow-2xl p-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-6">Entrar na plataforma</h2>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                E-mail
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="seu@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="login-senha" className="block text-sm font-medium text-slate-700 mb-1.5">
                Senha
              </label>
              <input
                id="login-senha"
                name="password"
                type="password"
                autoComplete="current-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="••••••"
                required
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={lembrar}
                onChange={(e) => {
                  const v = e.target.checked;
                  setLembrar(v);
                  if (!v) gravarCreds(null);
                }}
                className="accent-blue-600 mt-0.5"
              />
              <span className="leading-snug">
                Lembrar e-mail e senha neste dispositivo
                <span className="block text-[11px] text-slate-400">
                  Use só em aparelhos pessoais — fica guardado localmente.
                </span>
              </span>
            </label>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !senha}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
